import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, TrendingUp, BarChart3, Package, Users, Tag, Save, Percent, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API}/api/book-purchase/admin/analytics`, { headers, credentials: 'include' });
      if (res.ok) {
        setAnalytics(await res.json());
      } else {
        toast.error('Access denied');
        navigate('/admin');
      }
    } catch (err) {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product.product_key);
    setEditValues({
      title: product.title,
      base_price: product.base_price,
      retail_price: product.retail_price || product.base_price,
      available: product.available,
      preorder: product.preorder,
      discount_eligible: product.discount_eligible,
    });
  };

  const handleSaveProduct = async (productKey) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API}/api/book-purchase/admin/products/${productKey}`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify(editValues),
      });
      if (res.ok) {
        toast.success('Product updated');
        setEditingProduct(null);
        fetchAnalytics();
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      toast.error('Error saving product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-amber-600/50 animate-pulse">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;
  const { summary, product_margins, tier_distribution } = analytics;

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" data-testid="admin-pricing-page">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 mb-6 transition-colors"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-amber-500" />
              Pricing & Profitability Manager
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage pricing, analyze profits, and ensure consistency across tiers</p>
          </div>
        </div>

        {/* Revenue Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="revenue-summary">
          {[
            { label: 'Total Orders', value: summary.total_orders, icon: ShoppingCart, color: 'text-blue-400' },
            { label: 'Completed', value: summary.completed_orders, icon: Package, color: 'text-emerald-400' },
            { label: 'Revenue', value: `$${summary.total_revenue.toFixed(2)}`, icon: DollarSign, color: 'text-amber-400' },
            { label: 'Discounts Given', value: `$${summary.total_discount_given.toFixed(2)}`, icon: Tag, color: 'text-rose-400' },
            { label: 'Avg Discount', value: `${summary.avg_discount_percent}%`, icon: Percent, color: 'text-purple-400' },
          ].map((card, i) => (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <card.icon className={`h-4 w-4 ${card.color}`} />
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{card.label}</span>
              </div>
              <p className={`text-2xl font-bold font-mono ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Tier Distribution */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8" data-testid="tier-distribution">
          <h2 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-2">
            <Users className="h-4 w-4" /> User Tier Distribution
          </h2>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(tier_distribution).map(([tier, count]) => (
              <div key={tier} className="flex items-center gap-3 bg-gray-800/50 rounded-lg px-4 py-2">
                <div className={`w-3 h-3 rounded-full ${
                  tier === 'scholar' ? 'bg-purple-500' :
                  tier === 'berean' ? 'bg-blue-500' :
                  tier === 'church' ? 'bg-emerald-500' : 'bg-amber-500'
                }`} />
                <span className="text-sm font-medium capitalize text-gray-300">{tier}</span>
                <span className="text-lg font-mono font-bold text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Product Pricing & Margins Table */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-8" data-testid="product-margins">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="text-sm font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Product Pricing & Margins
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  {['Product', 'Retail Price', 'Our Price', 'Est. Cost', 'Margin', 'Margin %', 'Units Sold', 'Revenue', 'Discount', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {product_margins.map(pm => (
                  <tr key={pm.product_key} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3">
                      {editingProduct === pm.product_key ? (
                        <input
                          value={editValues.title}
                          onChange={e => setEditValues(v => ({ ...v, title: e.target.value }))}
                          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-full"
                        />
                      ) : (
                        <span className="text-white font-medium">{pm.title}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {editingProduct === pm.product_key ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues.retail_price}
                          onChange={e => setEditValues(v => ({ ...v, retail_price: parseFloat(e.target.value) || 0 }))}
                          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-20"
                        />
                      ) : (
                        <span className="text-rose-300 line-through">${(pm.retail_price || pm.base_price).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {editingProduct === pm.product_key ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues.base_price}
                          onChange={e => setEditValues(v => ({ ...v, base_price: parseFloat(e.target.value) || 0 }))}
                          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-20"
                        />
                      ) : (
                        <span className="text-amber-300">${pm.base_price.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-400">${pm.est_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono text-emerald-400">${pm.margin.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-mono font-bold ${pm.margin_pct >= 60 ? 'text-emerald-400' : pm.margin_pct >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>
                        {pm.margin_pct}%
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-300">{pm.units_sold}</td>
                    <td className="px-4 py-3 font-mono text-blue-300">${pm.total_revenue.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      {editingProduct === pm.product_key ? (
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editValues.discount_eligible}
                            onChange={e => setEditValues(v => ({ ...v, discount_eligible: e.target.checked }))}
                            className="accent-amber-500"
                          />
                          <span className="text-xs text-gray-400">Eligible</span>
                        </label>
                      ) : (
                        pm.discount_eligible
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400 border border-emerald-700/30">Yes</span>
                          : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-500 border border-gray-700/30">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingProduct === pm.product_key ? (
                        <div className="flex gap-1">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editValues.available}
                              onChange={e => setEditValues(v => ({ ...v, available: e.target.checked }))}
                              className="accent-amber-500"
                            />
                            <span className="text-xs text-gray-400">Avail</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer ml-2">
                            <input
                              type="checkbox"
                              checked={editValues.preorder}
                              onChange={e => setEditValues(v => ({ ...v, preorder: e.target.checked }))}
                              className="accent-blue-500"
                            />
                            <span className="text-xs text-gray-400">Pre</span>
                          </label>
                        </div>
                      ) : (
                        pm.available
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400">Live</span>
                          : pm.preorder
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-400">Pre-order</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-500">Unavailable</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingProduct === pm.product_key ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleSaveProduct(pm.product_key)}
                            className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs h-7 px-2"
                            data-testid={`save-${pm.product_key}`}
                          >
                            <Save className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingProduct(null)}
                            className="text-gray-400 text-xs h-7 px-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditProduct(pm)}
                          className="text-amber-500 hover:text-amber-400 text-xs h-7 px-2"
                          data-testid={`edit-${pm.product_key}`}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pricing Consistency Check */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="consistency-check">
          <h2 className="text-sm font-mono uppercase tracking-wider text-gray-400 mb-4">Pricing Consistency Analysis</h2>
          <div className="space-y-3">
            {product_margins.map(pm => {
              const issues = [];
              if (pm.base_price <= 0) issues.push({ text: 'Price is zero or negative', severity: 'error' });
              if (pm.margin_pct < 40) issues.push({ text: `Low margin (${pm.margin_pct}%)`, severity: 'warning' });
              if (pm.discount_eligible && pm.margin_pct < 50) issues.push({ text: 'Discount-eligible with thin margin - max discount may cause loss', severity: 'warning' });
              if (!pm.available && !pm.preorder) issues.push({ text: 'Not available and not pre-orderable', severity: 'info' });

              return (
                <div key={pm.product_key} className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/30">
                  <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                    <Package className={`h-4 w-4 ${issues.length === 0 ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{pm.title}</p>
                    {issues.length === 0 ? (
                      <p className="text-xs text-emerald-400">Pricing looks healthy</p>
                    ) : (
                      <div className="space-y-1 mt-1">
                        {issues.map((issue, i) => (
                          <p key={i} className={`text-xs ${
                            issue.severity === 'error' ? 'text-rose-400' :
                            issue.severity === 'warning' ? 'text-amber-400' : 'text-gray-400'
                          }`}>
                            {issue.severity === 'error' ? '!' : issue.severity === 'warning' ? '!' : 'i'} {issue.text}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPricing;
